import { LoadingButton } from "@mui/lab";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { MaterialReactTable } from "material-react-table";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { useSelectCEDialog } from "render-hooks/useSelectCEDialog";
import {
  isApiFailedError,
} from "services/common/type-guard";
import { usePrepareAndDownloadFileMutation, usePrepareFileMutation } from "services/file";
import { useLazyGetPEInfoQuery } from "services/pe";
import { useGetWorkflowFileTypeQuery } from "services/workflow";

import {
  JobAttributeEnum,
  JobDetailFileTypeEnum,
  JobDetailNextActionEnum,
  JobType,
  PEStatus,
  WorkflowType,
} from "../../../api/api";
import { defs } from "../../../common/const_defs";
import { AdcTitle1, AdcTitle2 } from "../../../components/atoms/AdcTitle";
import { NoneWrapper } from "../../../components/atoms/NoneWrapper";
import { CommonSelect } from "../../../components/molecular/CommonSelect";

import type { Job, JobDetail, PE } from "../../../api/api";
import type { SelectNode } from "../../../common/type_defs";
import type { RowSelectionState, Updater } from "@tanstack/react-table";
import type { MRT_ColumnDef, MRT_Row } from "material-react-table";
import type { FC } from "react";

const EXCEL_REPORT_STEP = 'EXCEL_REPORT_STEP'
const EXCEL_REPORT_JOB = 'EXCEL_REPORT_JOB'
const EXCEL_REPORT_CE = 'EXCEL_REPORT_CE'

const isCESelectNotRequired = (job: Job): boolean =>
  job.workflowStep === "DONE" ||
  job.workflowType === WorkflowType.NewReport ||
  job.workflowType === WorkflowType.Tango ||
  (job.workflowType === WorkflowType.TopSide && job.workflowStep === "INPUT") ||
  (job.workflowType?.startsWith("SUBMITTING_FILE") === true && job.workflowStep === "INPUT");

const getColumnDef = (
  onAction: (verb: string, detail: unknown) => void
): MRT_ColumnDef<JobDetail>[] => [
    {
      accessorKey: "companyName",
      header: "Company",
      size: 80,
      Cell: ({ row }) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-all" }}>{row.original.companyName}</div>
      ),
    },
    {
      accessorKey: "country",
      header: "Country",
      size: 120,
    },
    {
      accessorKey: "PECode",
      header: "CE Code",
      size: 120,
    },
    {
      accessorKey: "companyCategory",
      header: "Company Category",
      size: 160,
    },
    {
      accessorKey: "PECode",
      header: "Action",
      size: 160,
      Cell: ({ cell, row }) => (
        <Button
          size="small"
          color="primary"
          variant="text"
          aria-label="error information"
          onClick={() => onAction(defs.kAct_ViewPeStatus, row.original.PECode)}
          sx={{ m: 0, p: 0, minWidth: 50, width: 30 }}
        >
          Detail
        </Button>
      ),
    },
  ];
type JobDetailListProps = {
  isLoading: boolean;
  companies: PE[];
  job: Job;
  onAction: (act: string, vd: unknown) => void;
  onSelChanged: (sel: RowSelectionState) => void;
};

const JobDetailList: FC<JobDetailListProps> = ({
  companies,
  onAction,
  onSelChanged,
  isLoading,
  job,
}) => {
  const columns = useMemo<MRT_ColumnDef<JobDetail>[]>(() => getColumnDef(onAction), [onAction]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleRowSelChanged = useCallback(
    (rowSel: Updater<RowSelectionState>) => {
      const newSel = rowSel instanceof Function ? rowSel(rowSelection) : rowSel;
      setRowSelection(newSel);
      onSelChanged(newSel);
    },
    [onSelChanged, rowSelection]
  );
  return (
    <Paper elevation={0} sx={{}}>
      <MaterialReactTable
        columns={columns}
        data={companies}
        initialState={{ density: "compact" }}
        enableColumnActions={false}
        enableColumnFilters={false}
        enablePagination={false}
        enableRowSelection={!isCESelectNotRequired(job)}
        getRowId={(rd, index: number, parentRow: MRT_Row<JobDetail>) => rd.PECode ?? ""}
        onRowSelectionChange={handleRowSelChanged}
        enableSorting
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableRowVirtualization
        state={{ isLoading, rowSelection }}
        muiTableContainerProps={{ sx: { height: 300 } }}
        muiTableBodyRowProps={{ hover: true }}
      />
    </Paper>
  );
};

const isSelectedBeyondGroupCode = (ceList: string[]): boolean => {
  const splitCEList = ceList.map((ce) => ce.split("-"));
  if (splitCEList.some((ce) => ce.length !== 3)) {
    return false;
  }

  const groups = splitCEList.map(([groupCode]) => groupCode);

  if (groups.every((group) => group === groups.at(0))) {
    return true;
  }
  return false;
};



type JobActionInitProps = {
  job: Job;
  setJob: (job: Job) => void;
  onAction: (act: string, data: unknown) => void;
};

const ExcelReportActionInitPanel: FC<JobActionInitProps> = ({ job, setJob, onAction }) => {
  const [selList, setSelList] = useState<RowSelectionState>({});
  const [fileType, setFileType] = useState<SelectNode>({ label: "", value: job.fileType ?? "" });
  const { enqueueSnackbar } = useSnackbar();
  const { openSelectCEDialog, renderSelectCEDialog } = useSelectCEDialog();

  const { data: workflowFileType } = useGetWorkflowFileTypeQuery(
    {
      workflowStep: job.workflowStep ?? "",
      workflowType: job.workflowType ?? "",
    },
    { skip: job.workflowType !== "STANDARD_TEMPLATE" }
  );
  const [getPEInfo] = useLazyGetPEInfoQuery();
  const [prepareFile, { isLoading: isPrepareFileLoading }] = usePrepareFileMutation();
  const [prepareAndDownloadFile] = usePrepareAndDownloadFileMutation();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [prepareSuccessed, setPrepareSuccesed] = useState(false);
  const [isCESelectNotRequired, setIsCESelectNotRequired] = useState<boolean>(false);

  const fileTypeSelectionList: SelectNode[] = useMemo(
    () =>
      workflowFileType?.map((fileType) => ({
        label: fileType.displayName,
        value: fileType.name,
      })) ?? [],
    [workflowFileType]
  );

  const checkDisableSelectCE = useCallback((): boolean => {
    if (job.attribute === JobAttributeEnum.Approve) {
      return false;
    }
    if (
      job.workflowType !== "STANDARD_TEMPLATE" ||
      job.workflowType?.startsWith("SUBMITTING_FILE_")
    ) {
      return true;
    }
    if (job.type === JobType.NotStarted) {
      return false;
    }

    return true;
  }, [job.attribute, job.type, job.workflowType]);


  const handleFileTypeChanged = useCallback((v: SelectNode) => {
    setFileType(v);
  }, []);

  const handleAction = useCallback(
    (act: string, data: unknown) => {
      if (act === defs.kAct_ViewPeStatus) {
        onAction(act, data);
      } else {
        // onAction('download', jobsSelected);
      }
    },
    [onAction]
  );

  const getFiscalYear = useCallback((date: Date): number => {
    if (date.getMonth() >= 4) {
      return date.getFullYear();
    }
    return date.getFullYear() - 1;
  }, []);

  const handleClickFilter = useCallback(async () => {

    let attribute: string | undefined;
    if (job.attribute) {
      if (job.attribute === JobAttributeEnum.Submit) {
        attribute = "INPUT_WAITING";
      }
      else {
        attribute = "APPROVE_WAITING";
      }
    } else {
      console.log("attribute is undefined");
      attribute = undefined;
    }

    const aryPE = await openSelectCEDialog({
      options: {
        workflowType: job.workflowType,
        workflowStep: job.workflowStep,
        fiscalYear: job.fiscalYear,
        fixVersion: job.fixVersion,
        jobStatus: attribute,
      },
    });

    let ary: PE[] = [];
    try {
      const getPEInfoResponse = await getPEInfo({
        PECodeList: aryPE,
        fiscalYear: job.fiscalYear!,
      }).unwrap();
      ary = getPEInfoResponse.PEList.filter((pe) => aryPE.includes(pe.PECode));
    } catch (e) { }
    const aryDetail: JobDetail[] = ary.map((pe: PE) => {
      const detail: JobDetail = {
        PECode: pe.PECode,
        companyName: pe.companyName,
        fileType: JobDetailFileTypeEnum.SummarySheet,
        fileName: `file-${pe.PECode}`,
        nextAction: JobDetailNextActionEnum.ProceedNextStep,
        PEStatus: PEStatus.NotStarted,
        // completedTime: "2023-07-23",
      };

      return detail;
    });
    setJob({ ...job, JobDetails: aryDetail });
  }, [getPEInfo, job, openSelectCEDialog, setJob]);

  const getSelectedItem = useCallback((): string[] => {
    const peList: string[] = [];
    for (const key in selList) {
      if (key in selList && selList[key]) {
        peList.push(key);
      }
    }
    return peList;
  }, [selList]);

  const handleClickPreset = useCallback(async () => {
    console.log(job);

    const peList = getSelectedItem();
    const { fiscalYear } = job;

    if (!isSelectedBeyondGroupCode(peList)) {
      enqueueSnackbar("Preset across group codes is not allowed", { variant: "error" });
      return;
    }
    try {
      await prepareFile({
        fiscalYear,
        fixVersion: job.fixVersion,
        workflowType: job.workflowType,
        workflowStep: EXCEL_REPORT_STEP,
        fileType: fileType.value,
        CECodeList: peList,
        name: EXCEL_REPORT_JOB,
        presetType: "NEW",
      }).unwrap();
      enqueueSnackbar(`Succeeded to prepare file.`, { variant: "success" });
      setPrepareSuccesed(true)
    } catch (error) {
      if (isApiFailedError(error)) {
        enqueueSnackbar(error.data.message, { variant: "error" });
      } else {
        enqueueSnackbar("Failed to prepare file.", { variant: "error" });
      }
      // return;
    }

  }, [enqueueSnackbar, fileType.value, getSelectedItem, job, prepareFile]);

  const handleClickDownload = useCallback(async () => {
    setIsLoadingDownload(true);

    const peList = getSelectedItem();
    const { fiscalYear } = job;

    if (prepareSuccessed) {
      // 点完preset按钮再点击download
      try {
        await prepareAndDownloadFile({
          prepareRequest: {
            fiscalYear,
            fixVersion: job.fixVersion,
            workflowType: job.workflowType,
            workflowStep: EXCEL_REPORT_STEP,
            name: EXCEL_REPORT_JOB,
            fileType: fileType.value,
            CECodeList: peList
          },
          generateDownloadURLRequest: {
            fileType: fileType.value,
            category: "WORKFLOW",
          },
        });
      } catch (error) {
        setIsLoadingDownload(false);
        return;
      }

    } else {
      // 直接点击download
      try {
        await prepareAndDownloadFile({
          prepareRequest: {
            fiscalYear,
            fixVersion: job.fixVersion,
            workflowType: job.workflowType,
            workflowStep: EXCEL_REPORT_STEP,
            name: EXCEL_REPORT_JOB,
            fileType: fileType.value,
            CECodeList: [EXCEL_REPORT_CE],
          },
          generateDownloadURLRequest: {
            fileType: fileType.value,
            category: "WORKFLOW",
          },
        });
      } catch (error) {
        setIsLoadingDownload(false);
        return;
      }
    }
    setIsLoadingDownload(false);
  }, [fileType.value, getSelectedItem, job, prepareAndDownloadFile, prepareSuccessed]);

  const handleSelChanged = useCallback(
    (sel: RowSelectionState) => {
      setSelList(sel);
      setJob({
        ...job,
        selList: sel,
      });
    },
    [job, setJob]
  );

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Grid
        container
        flexDirection={{ xs: "column", sm: "row" }}
        sx={{ px: 1, fontSize: "12px" }}
        spacing={1}
      >
        <Grid xs={12} sm={12} sx={{ mb: 1 }}>
          <AdcTitle1 component="span">Excel Report Management</AdcTitle1>
          <Typography component="span" sx={{ mx: 1, fontSize: "15px", color: "#a0a0a0" }}>
            - {job.workflowType ?? ""}
          </Typography>
        </Grid>
        <Grid xs={12} sm={12} sx={{ mt: 1 }}>
          <AdcTitle2 component="span">■ Target CE</AdcTitle2>
        </Grid>
        <Grid xs={12} sm={12} sx={{ pl: 3 }}>
          <Stack
            direction="row"
            sx={{ width: "100%", gap: "1.5rem", justifyContent: "flex-start" }}
          >
            <NoneWrapper show={job.workflowType === WorkflowType.StandardTemplate}>
              <CommonSelect
                isDisabled={
                  (job.fileType !== undefined && job.fileType !== "") ||
                  prepareSuccessed
                }
                value={fileType}
                options={fileTypeSelectionList}
                onChanged={handleFileTypeChanged}
                sxx={{ minWidth: 200 }}
              />
            </NoneWrapper>
            <NoneWrapper show={job.workflowType === WorkflowType.StandardTemplate}>
              <LoadingButton
                variant="outlined"
                disabled={
                  !Object.keys(selList).length ||
                  // fileType?.value === "1CE_STANDARD_TEMPLATE" ||
                  job.type !== JobType.NotStarted ||
                  prepareSuccessed
                }
                onClick={handleClickPreset}
                loading={isPrepareFileLoading}
              >
                Preset
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                onClick={handleClickDownload}
                loading={isLoadingDownload}
              >
                Download
              </LoadingButton>
            </NoneWrapper>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              disabled={checkDisableSelectCE()}
              onClick={handleClickFilter}
            >
              Select CE
            </Button>
          </Stack>
        </Grid>
        <Grid xs={12} sm={12} sx={{ pl: 3 }}>
          <JobDetailList
            companies={job.JobDetails ?? []}
            onAction={handleAction}
            onSelChanged={handleSelChanged}
            isLoading={false}
            job={job}
          />
        </Grid>
      </Grid>
      {renderSelectCEDialog()}
    </Paper>
  );
};

export default ExcelReportActionInitPanel;
export { ExcelReportActionInitPanel };
